<script setup>
  import { onMounted, ref, watch } from 'vue'
  import { useRouter } from 'vue-router'
  import { apiService } from '@/services/api'
  import { useDialogStore } from '@/stores/dialogStore'
  import { useUserStore } from '@/stores/userStore'
  import { convertToHalfWidthNumber } from '@/components/logics/Util'
  import Webpass from '@laragear/webpass'
  import FooterNoneLogin from '@/components/FooterNoneLogin.vue'

  const router = useRouter()

  const dialogStore = useDialogStore()
  const userStore = useUserStore()

  const showPassword = ref(false)

  // フォーム用データ
  const form = ref(null)
  const formData = ref({
    email: '',
    password: '',
    otpCode: '',
  })

  // 二段階認証用
  const codedata = ref([])
  const firstInputEl = ref(null)
  const emit = defineEmits(['update:modelValue'])
  const handleOtpInput = e => {
    if (e.data && e.target.nextElementSibling) {
      e.target.nextElementSibling.focus()
    } else if (e.data == null && e.target.previousElementSibling) {
      e.target.previousElementSibling.focus()
    }
  }
  const handlePaste = e => {
    const pasteData = e.clipboardData.getData('text')
    let nextEl = firstInputEl.value[0].nextElementSibling
    for (let i = 1; i < pasteData.length; i++) {
      if (nextEl) {
        codedata.value[i] = pasteData[i]
        nextEl = nextEl.nextElementSibling
      }
    }
  }
  watch(
    () => codedata,
    newVal => {
      if (newVal.value != '' && newVal.value.length === 6 && !newVal.value.includes('')) {
        emit('update:modelValue', Number(newVal.value.join('')))
      } else {
        emit('update:modelValue', null)
      }
    },
    { deep: true }
  )

  const showPopupCode = ref(false)
  const closePopupCode = event => {
    if (!event.target.closest('.popup-code-content')) {
      showPopupCode.value = false
      codedata.value = []
      formData.value.otpCode = '' // 認証コードをクリア
    }
  }

  // ログイン処理
  const login = async e => {
    e.preventDefault()

    const submitId = e.submitter.id
    if (submitId === 'login') {
      await passwordLogin()
    }
  }
  // パスワードログイン
  const passwordLogin = async () => {
    try {
      const response = await apiService.post('/api/login', formData.value, {
        showErrorDialog: false,
      })
      userStore.currentUser = response.user
      router.push(response.redirect)
    } catch (error) {
      // エラーレスポンスから authCodeRequired を確認
      if (error.response && error.response.data && error.response.data.authCodeRequired) {
        // 二段階認証コード入力ポップアップを表示
        showPopupCode.value = true
        codedata.value = [] // 入力フィールドをリセット
        firstInputEl.value[0].focus() // 最初の入力フィールドにフォーカスを当てる
      } else {
        // 通常のエラーメッセージを表示
        const errorMessage = error.response?.data?.message || error.message || 'ログインに失敗しました。'
        await dialogStore.showAlertDialog('ログインエラー', errorMessage)
      }
    }
  }

  // 二段階認証ログイン
  const otpSubmit = async () => {
    if (codedata.value.length !== 6) {
      await dialogStore.showAlertDialog('ディーモ エラー', '6桁の数字を入力してください。')
      return
    }
    formData.value.otpCode = convertToHalfWidthNumber(codedata.value.join(''))
    try {
      await passwordLogin()
    } catch (error) {
      // 認証コードでエラーが発生した場合、認証コードをクリア
      formData.value.otpCode = ''
      throw error
    }
  }

  // パスキーログイン
  const passkeyLogin = async () => {
    try {
      if (Webpass.isUnsupported()) {
        await dialogStore.showAlertDialog('エラー', 'このブラウザはパスキー認証に対応していません。')
        return
      }
      // WebAuthnの認証処理
      const { user, success, error } = await Webpass.assert('/auth/assert-options', '/auth/assert')

      if (!success) {
        throw new Error(error)
      }

      // 現在のユーザーを取得
      await userStore.getCurrentUser()

      if (userStore.currentUser) {
        // ログイン成功時の処理
        router.push('/dashboard')
      } else {
        // ユーザーが見つからない場合の処理
        await dialogStore.showAlertDialog('エラー', 'ユーザーが見つかりません。')
      }
    } catch (error) {
      await dialogStore.showAlertDialog('エラー', 'パスキー認証に失敗しました')
    }
  }

  onMounted(() => {
    userStore.currentUser = null
  })
</script>

<template>
  <header></header>
  <main class="login-main">
    <div class="login-content">
      <form @submit.prevent="login" ref="form" method="post">
        <h1><img class="logo" src="@/assets/img/login_logo.svg" alt="ディーモ for 社労士" /></h1>
        <h2>ログイン</h2>
        <div class="form-grid">
          <div class="login-input">
            <label>メールアドレス</label>
            <input type="text" name="email" id="email" v-model="formData.email" required />
          </div>
          <div class="login-input type-password">
            <label>パスワード</label>
            <input v-if="showPassword" type="text" v-model="formData.password" />
            <input v-else type="password" name="passwd" id="passwd" v-model="formData.password" />
            <button type="button" class="show-password" @click="showPassword = !showPassword">
              <span class="material-symbols-outlined">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
            </button>
          </div>
          <button class="input-submit" id="login" type="submit" :disabled="formData.email == '' || formData.password == ''">サインイン</button>
          <div class="forgot-password">
            <router-link to="/reset-password-request">パスワードをお忘れの方はこちら</router-link>
          </div>
          <button type="button" id="passkey-login" class="input-submit" :disabled="formData.id == ''" @click="passkeyLogin">パスキーでサインイン</button>
        </div>
        <input class="hidden" type="text" name="otp" autocomplete="one-time-code" v-model="formData.otpCode" />
        <input class="hidden" type="text" name="passkey" autocomplete="one-time-code" v-model="formData.passkeyValue" />

        <hr />
        <!-- <h3>オプションメニュー</h3>

        <div class="form-grid">
          <button type="button" class="input-submit" @click="showPopupCode = !showPopupCode" :disabled="formData.id == '' || formData.password == ''">二段階認証入力</button>
          <button type="button" id="passkey-login" class="input-submit" :disabled="formData.id == ''" @click="passkeyLogin">パスキーでサインイン</button>
        </div> -->
        <div class="privacy">
          <a href="https://ten-eight.site/privacy-policy/" target="_blank">プライバシーポリシー</a>
        </div>
        <div class="egov">
          <div>ディーモ for 社労士はデジタル庁の最終試験を通過しております。</div>
          <div class="description">※デジタル庁の最終確認試験は、民間事業者が開発したソフトウェアを使用してe-Gov電子申請サービスが正常動作確認することを目的としております。民間事業者が開発したソフトウェアの正常動作の確認を目的とするものではありません。</div>
        </div>
        <div class="form-grid"><button type="button" class="input-submit" @click="router.push('/join')">新規登録</button></div>
      </form>
    </div>
  </main>
  <FooterNoneLogin />
  <div class="popup-code" v-if="showPopupCode" @click="closePopupCode">
    <div class="popup-code-content">
      <h2>認証コードを入力</h2>
      <div class="form-grid">
        <div class="login-code" @input="handleOtpInput">
          <template v-for="field in 6" :key="field">
            <input v-model="codedata[field - 1]" ref="firstInputEl" type="text" maxlength="1" @paste="field === 1 && handlePaste($event)" />
          </template>
        </div>
        <button type="button" class="input-submit" @click="otpSubmit">サインイン</button>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
  .forgot-password {
    text-align: center;
    color: var(--active-color);
    font-size: 12px;
    text-decoration: underline;
    cursor: pointer;
    transition: opacity 0.3s;
  }
</style>
